import { createServerFn } from "@tanstack/react-start";
import type {
    DiscoveryListingCard,
    SearchExecutionContext,
    WagsProviderType,
    WagsSearchPuppyListingsInput,
} from "@wags/discovery/index.ts";
import { discoverySearchParamsSchema } from "@wags/discovery/index.ts";
import { resolveMaxDistanceMiles } from "@wags/discovery/pure.ts";
import type { BoundaryUnknown } from "@wags/utils/boundary-unknown.ts";
import { getDiscovery } from "@/lib/core/discovery-core.server";
import type {
    BestMatchPreferences,
    SearchSortValue,
    UrlSearchParams,
} from "./search-params.contract.ts";

const DEFAULT_LIMIT = 50;

export type SearchParams = {
    lat: number;
    lng: number;
    locationSource?: UrlSearchParams["locationSource"];
    q?: string;
    feedContext?: string;
    maxDistanceMiles?: number;
    hardRadius?: boolean;
    breedSlugs?: string[];
    dogName?: string;
    breedType?: "purebred" | "mixed";
    providerName?: string;
    providerType?: WagsProviderType;
    size?: ("toy" | "small" | "medium" | "large" | "giant")[];
    coatType?: (
        | "smooth"
        | "short"
        | "medium"
        | "long"
        | "wire"
        | "curly"
        | "double"
        | "hairless"
    )[];
    sheddingLevel?: (
        | "none"
        | "minimal"
        | "moderate"
        | "heavy"
        | "seasonal_blowout"
    )[];
    hypoallergenic?: boolean;
    priceMin?: number;
    priceMax?: number;
    dobMin?: number;
    dobMax?: number;
    sex?: "male" | "female";
    friendlinessChildren?: RatingFilter[];
    friendlinessDogs?: RatingFilter[];
    friendlinessCats?: RatingFilter[];
    energyLevel?: RatingFilter[];
    trainability?: RatingFilter[];
    healthIssuesRisk?: RatingFilter[];
    excludeLitters?: boolean;
    sortBy?: SearchSortValue;
    bestMatchPreferences?: BestMatchPreferences;
    offset?: number;
    page?: number;
    limit?: number;
};

type RatingFilter = {
    value: "1" | "2" | "3" | "4" | "5";
    operator: "eq" | "gte" | "lte";
};

export type SearchResultItem = DiscoveryListingCard;

export type SearchResult = {
    results: SearchResultItem[];
    anchorIndex: number | null;
    offset: number;
    page: number;
    limit: number;
    total: number;
    prevOffset: number | null;
    nextOffset: number | null;
    nextPage: number | null;
    withinRangeCount: number | null;
    execution?: SearchExecutionContext;
};

export function buildDiscoverySearchRequest(data: SearchParams) {
    const limit = Math.max(1, data.limit ?? DEFAULT_LIMIT);
    const offset = Math.max(
        0,
        data.offset ?? Math.max(0, data.page ?? 0) * limit,
    );
    const page = Math.floor(offset / limit);
    const input: WagsSearchPuppyListingsInput = {
        limit,
        offset,
        location: {
            latitude: data.lat,
            longitude: data.lng,
            maxDistanceMiles: resolveMaxDistanceMiles(data.maxDistanceMiles),
            hardRadius: data.hardRadius,
        },
        breedSlugs: data.breedSlugs,
        dogName: data.dogName,
        breedType: data.breedType,
        providerName: data.providerName,
        providerType: data.providerType,
        sex: data.sex,
        price:
            data.priceMin !== undefined || data.priceMax !== undefined
                ? { min: data.priceMin, max: data.priceMax }
                : undefined,
        dob:
            data.dobMin !== undefined || data.dobMax !== undefined
                ? { min: data.dobMin, max: data.dobMax }
                : undefined,
        size: data.size,
        coatType: data.coatType,
        sheddingLevel: data.sheddingLevel,
        hypoallergenic: data.hypoallergenic,
        friendlinessChildren: data.friendlinessChildren,
        friendlinessDogs: data.friendlinessDogs,
        friendlinessCats: data.friendlinessCats,
        energyLevel: data.energyLevel,
        trainability: data.trainability,
        healthIssuesRisk: data.healthIssuesRisk,
        excludeLitters: data.excludeLitters,
        sortBy: data.sortBy,
        bestMatchPreferences: data.bestMatchPreferences,
    };

    return { page, limit, offset, input };
}

export const getTotalDogCount = createServerFn({ method: "GET" })
    .inputValidator(() => undefined)
    .handler(async () => {
        const discovery = await getDiscovery();
        return await discovery.search.countAllAvailable();
    });

export const getMatchedDogCount = createServerFn({ method: "GET" })
    .inputValidator((data: BoundaryUnknown) =>
        discoverySearchParamsSchema.parse(data),
    )
    .handler(async ({ data }) => {
        const { input } = buildDiscoverySearchRequest(data);
        const discovery = await getDiscovery();
        return await discovery.search.countMatched(input);
    });

export const searchListings = createServerFn({ method: "GET" })
    .inputValidator((data: BoundaryUnknown) =>
        discoverySearchParamsSchema.parse(data),
    )
    .handler(async ({ data }): Promise<SearchResult> => {
        const { page, limit, offset, input } =
            buildDiscoverySearchRequest(data);
        const result = await (await getDiscovery()).search.search(input);
        const total = result.total;

        return {
            anchorIndex: null,
            results: result.listings,
            offset: result.offset,
            page,
            limit,
            total,
            prevOffset:
                result.offset > 0 ? Math.max(0, result.offset - limit) : null,
            nextOffset:
                result.offset + result.listings.length < total
                    ? result.offset + result.listings.length
                    : null,
            nextPage:
                (result.hasNextPage ?? offset + result.listings.length < total)
                    ? page + 1
                    : null,
            withinRangeCount: result.withinRangeCount,
        };
    });
